/**
 * SECTOR SEVEN — tube geometry.
 *
 * The tube is a polygon centered at the canvas vanishing point. Each
 * EDGE of the polygon is a "lane" — enemies climb from the vanishing
 * point along their lane's center line out to the rim, where the
 * player sits.
 *
 * Coordinate model:
 *   - Vanishing point (VP): canvas center, (CANVAS_W/2, CANVAS_H/2).
 *   - Rim radius: distance from VP to a rim vertex.
 *   - Lane index i ∈ [0, N): the polygon EDGE between rim vertex i and
 *     rim vertex (i+1) mod N. Lane 0 is the top edge, going clockwise.
 *   - `depth`: scalar in [0, 1] interpolating from the rim (0) toward
 *     the VP (1). depth=0 is the player's plane; depth=1 is dead
 *     center.
 *
 * The lane center line — what enemies actually travel — is the
 * midpoint of the two lane edges (each of which is itself a segment
 * from a rim vertex to the VP) at any given depth. Equivalently:
 * `lerp(rimMidpoint(i), VP, depth)`.
 *
 * MVP ships hex (level 1, 4, 7…) + octagon (level 2, 5, 8…),
 * alternating per level. Star + cross-shape geometries are deferred to
 * a followup story — they're pure coordinate work, no gameplay diff.
 */

export const CANVAS_W = 960;
export const CANVAS_H = 540;
export const VP_X = CANVAS_W / 2;
export const VP_Y = CANVAS_H / 2;

/** Rim radius — the polygon is sized so it leaves room for the HUD on
 *  top/bottom and chrome on the sides. Symmetric so the math is clean. */
export const RIM_RADIUS = 220;

/** Number of lanes by level. Cycles hex/octagon. */
export function laneCountForLevel(level: number): number {
  return level % 2 === 1 ? 6 : 8;
}

/**
 * Vertex on the rim polygon at index `i` (0..laneCount-1). Vertex 0 is
 * the top-most point. Vertices are ordered clockwise.
 */
export function rimVertex(
  laneCount: number,
  i: number
): { x: number; y: number } {
  const angle = (i * Math.PI * 2) / laneCount - Math.PI / 2;
  return {
    x: VP_X + Math.cos(angle) * RIM_RADIUS,
    y: VP_Y + Math.sin(angle) * RIM_RADIUS,
  };
}

/**
 * Midpoint of the rim edge for lane `i` — equivalently, midpoint
 * between rimVertex(i) and rimVertex((i+1) % laneCount). This is where
 * enemies surface at depth=0 and where the player's defender sits when
 * standing on lane i.
 */
export function laneRimMidpoint(
  laneCount: number,
  i: number
): { x: number; y: number } {
  const a = rimVertex(laneCount, i);
  const b = rimVertex(laneCount, (i + 1) % laneCount);
  return {
    x: (a.x + b.x) / 2,
    y: (a.y + b.y) / 2,
  };
}

/**
 * The point on lane `i`'s CENTER LINE at the given depth. depth=0 is
 * the rim midpoint; depth=1 is the vanishing point. Linear interp.
 *
 * Enemies render their bodies centered at `laneCenter(i, e.depth)` and
 * scale by `1 - e.depth` so they shrink toward the VP (perspective).
 */
export function laneCenter(
  laneCount: number,
  i: number,
  depth: number
): { x: number; y: number } {
  const rim = laneRimMidpoint(laneCount, i);
  return {
    x: rim.x + (VP_X - rim.x) * depth,
    y: rim.y + (VP_Y - rim.y) * depth,
  };
}

/**
 * The two corner points of lane `i` at a given depth — the inner-edge
 * left + right corners. Useful for drawing the lane's two side rails.
 *
 * Returns { left, right } where left/right are the projected positions
 * of rimVertex(i) and rimVertex(i+1) interpolated toward VP by `depth`.
 */
export function laneCorners(
  laneCount: number,
  i: number,
  depth: number
): {
  left: { x: number; y: number };
  right: { x: number; y: number };
} {
  const a = rimVertex(laneCount, i);
  const b = rimVertex(laneCount, (i + 1) % laneCount);
  return {
    left: {
      x: a.x + (VP_X - a.x) * depth,
      y: a.y + (VP_Y - a.y) * depth,
    },
    right: {
      x: b.x + (VP_X - b.x) * depth,
      y: b.y + (VP_Y - b.y) * depth,
    },
  };
}

/** Angular center of lane `i` in screen space — useful for rotating
 *  enemy + spike sprites so they orient along their lane axis. */
export function laneAngle(laneCount: number, i: number): number {
  // The midpoint of the rim edge is on the line from VP to that
  // midpoint. The lane's outward direction is that vector.
  const mid = laneRimMidpoint(laneCount, i);
  return Math.atan2(mid.y - VP_Y, mid.x - VP_X);
}
